import React, { useState, useEffect } from 'react';
import { X, AlertTriangle, AlertCircle, Info } from 'lucide-react';
import { validateShortcut } from '../lib/validators';
import { MAX_SHORTCUT_LENGTH, MAX_EXPANSION_LENGTH } from '../lib/constants';
import { Shortcut } from '../types';
import './NewShortcutModal.css';

interface NewShortcutModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (shortcut: string, expansion: string, isTemplate: boolean) => Promise<void>;
  editingShortcut?: Shortcut | null;
}

export function NewShortcutModal({ isOpen, onClose, onSave, editingShortcut }: NewShortcutModalProps) {
  const [shortcut, setShortcut] = useState('');
  const [expansion, setExpansion] = useState('');
  const [isTemplate, setIsTemplate] = useState(false);
  const [validation, setValidation] = useState<ReturnType<typeof validateShortcut> | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (editingShortcut) {
      setShortcut(editingShortcut.shortcut);
      setExpansion(editingShortcut.expansion);
      setIsTemplate(editingShortcut.isTemplate || false);
    } else {
      setShortcut('');
      setExpansion('');
      setIsTemplate(false);
    }
    setValidation(null);
    setError(null);
  }, [editingShortcut, isOpen]);

  const handleShortcutChange = (value: string) => {
    const trimmedValue = value.trim().toLowerCase();
    setShortcut(trimmedValue);
    
    if (trimmedValue) {
      setValidation(validateShortcut(trimmedValue));
    } else {
      setValidation(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!shortcut || !expansion) {
      setError('Please fill in all fields');
      return;
    }
    
    if (validation && !validation.valid) {
      setError(validation.message || 'Invalid shortcut');
      return;
    }
    
    setIsSubmitting(true);
    setError(null);
    
    try {
      await onSave(shortcut, expansion, isTemplate);
      onClose();
    } catch (error: any) {
      console.error('Error saving shortcut:', error);
      setError(error.message || 'Failed to save shortcut');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const getValidationIcon = () => {
    if (!validation) return null;
    
    switch (validation.severity) {
      case 'error':
        return <AlertCircle className="validation-icon error" size={20} />;
      case 'warning':
        return <AlertTriangle className="validation-icon warning" size={20} />;
      case 'info':
        return <Info className="validation-icon info" size={20} />;
      default:
        return null;
    }
  };

  return (
    <div 
      className="modal-overlay" 
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div 
        className="modal-content" 
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            onClose();
          }
        }}
      >
        <div className="modal-header">
          <h2 id="modal-title">{editingShortcut ? 'Edit Shortcut' : 'Create New Shortcut'}</h2>
          <button 
            className="close-btn" 
            onClick={onClose}
            aria-label="Close modal"
          >
            <X size={20} />
          </button>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="shortcut">Shortcut</label>
            <div className="input-with-validation">
              <input
                id="shortcut"
                type="text"
                value={shortcut}
                onChange={(e) => handleShortcutChange(e.target.value)}
                maxLength={MAX_SHORTCUT_LENGTH}
                placeholder="e.g., pt, dx, rx"
                autoFocus
                disabled={!!editingShortcut}
              />
              {getValidationIcon()}
            </div>
            {validation?.message && (
              <p className={`validation-message ${validation.severity}`}>
                {validation.message}
              </p>
            )}
          </div>
          
          <div className="form-group">
            <label htmlFor="expansion">Expands to</label>
            <textarea
              id="expansion"
              value={expansion}
              onChange={(e) => setExpansion(e.target.value)}
              maxLength={MAX_EXPANSION_LENGTH}
              placeholder="e.g., patient, diagnosis, prescription"
              rows={3}
            />
            <p className="char-count">
              {expansion.length}/{MAX_EXPANSION_LENGTH} characters
            </p>
          </div>
          
          {!editingShortcut && (
            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={isTemplate}
                  onChange={(e) => setIsTemplate(e.target.checked)}
                />
                <span>User-specific template</span>
              </label>
              <p className="help-text">
                Templates allow each user to have their own expansion value
              </p>
            </div>
          )}
          
          {error && (
            <div className="error-message">
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}
          
          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn btn-primary" 
              disabled={isSubmitting || !shortcut || !expansion || (validation ? !validation.valid : false)}
            >
              {isSubmitting ? 'Saving...' : (editingShortcut ? 'Update' : 'Create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}